import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Switch, Tag, message, Collapse, Row, Col, Divider, Tooltip, Alert, Popconfirm } from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  DatabaseOutlined, 
  FunctionOutlined,
  FileTextOutlined, 
  BarChartOutlined, 
  SettingOutlined,
  InfoCircleOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import CustomDimensionModal from './CustomDimensionModal';
import MetricConfigModal from './MetricConfigModal';
import { 
  defaultPresetDimensions, 
  presetMetricTemplate,
  getDefaultConfig,
  createNewCustomDimension,
  createNewCustomMetric 
} from '../data/defaultDimensionConfig';

const { Panel } = Collapse;

/**
 * 维度指标配置组件
 * 
 * 支持两种数据格式：
 * - 新格式：presetDimensions/customDimensions（按维度组织）
 * - 旧格式：presetMetrics/customMetrics（按指标组织）
 * 
 * 新格式结构：
 * presetDimensions: [
 *   {
 *     key: 'document',
 *     name: '单据维度',
 *     type: 'system',
 *     enabled: true,
 *     presetMetrics: [{ key, name, enabled, description }],
 *     customMetrics: []
 *   }
 * ]
 * 
 * customDimensions: [
 *   {
 *     key: 'custom_dim',
 *     name: '自定义维度',
 *     type: 'custom',
 *     baseOn: ['document'],
 *     enabled: true,
 *     presetMetrics: [...],
 *     customMetrics: [...]
 *   }
 * ]
 */
const DimensionMetricConfig = ({
  initialData = {},
  onChange,
  readOnly = false
}) => {
  // 状态
  const [presetDimensions, setPresetDimensions] = useState([]);
  const [customDimensions, setCustomDimensions] = useState([]);
  
  // 弹窗状态
  const [dimensionModalVisible, setDimensionModalVisible] = useState(false);
  const [editingDimension, setEditingDimension] = useState(null);
  const [metricModalVisible, setMetricModalVisible] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [currentDimensionKey, setCurrentDimensionKey] = useState(null);
  const [currentDimensionType, setCurrentDimensionType] = useState(null); // 'preset' or 'custom'
  
  // 折叠面板状态
  const [activePresetKeys, setActivePresetKeys] = useState(['document', 'rule_avg', 'rule_detail']);
  const [activeCustomKeys, setActiveCustomKeys] = useState([]);

  // 初始化数据
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      // 检查数据格式
      if (initialData.presetDimensions || initialData.customDimensions) {
        // 新格式
        setPresetDimensions(initialData.presetDimensions || []);
        setCustomDimensions(initialData.customDimensions || []);
      } else if (initialData.presetMetrics || initialData.customMetrics) {
        // 旧格式，需要转换
        const converted = convertFromOldFormat(initialData);
        setPresetDimensions(converted.presetDimensions);
        setCustomDimensions(converted.customDimensions);
      } else {
        // 使用默认配置
        initDefaultConfig();
      }
    } else {
      // 使用默认配置
      initDefaultConfig();
    }
  }, [initialData]);

  // 初始化默认配置
  const initDefaultConfig = () => {
    const defaultConfig = getDefaultConfig();
    setPresetDimensions(defaultConfig.presetDimensions);
    setCustomDimensions(defaultConfig.customDimensions);
  };

  // 从旧格式转换为新格式
  const convertFromOldFormat = (oldData) => {
    const presetMetrics = oldData.presetMetrics || [];
    const customMetrics = oldData.customMetrics || [];
    
    // 构建维度级别的配置
    const dimensionConfig = {
      document: { presetMetrics: [], customMetrics: [] },
      rule_avg: { presetMetrics: [], customMetrics: [] },
      rule_detail: { presetMetrics: [], customMetrics: [] }
    };
    
    // 处理预设指标
    presetMetrics.forEach(metric => {
      const dimensions = metric.dimensions || [];
      dimensions.forEach(dim => {
        if (dim.enabled && dimensionConfig[dim.key]) {
          const existingPreset = dimensionConfig[dim.key].presetMetrics.find(m => m.key === metric.key);
          if (!existingPreset) {
            dimensionConfig[dim.key].presetMetrics.push({
              key: metric.key,
              name: metric.name,
              enabled: true,
              description: getMetricDescription(metric.key)
            });
          }
        }
      });
    });
    
    // 处理自定义指标 - 放入单据维度的customMetrics
    customMetrics.forEach(metric => {
      if (metric.enabled) {
        dimensionConfig.document.customMetrics.push({
          key: metric.code || metric.key,
          name: metric.name,
          enabled: true,
          description: metric.description || '',
          formula: metric.formula || ''
        });
      }
    });
    
    // 构建新的presetDimensions
    const newPresetDimensions = defaultPresetDimensions.map(dim => ({
      ...dim,
      presetMetrics: dimensionConfig[dim.key]?.presetMetrics.length > 0 
        ? dimensionConfig[dim.key].presetMetrics 
        : JSON.parse(JSON.stringify(presetMetricTemplate)),
      customMetrics: dimensionConfig[dim.key]?.customMetrics || []
    }));
    
    return {
      presetDimensions: newPresetDimensions,
      customDimensions: []
    };
  };

  // 获取指标描述
  const getMetricDescription = (key) => {
    const descriptions = {
      'no_risk_precision': '识别为无风险的样本中，实际无风险的比例',
      'no_risk_recall': '实际无风险的样本中，被正确识别的比例',
      'risk_precision': '识别为有风险的样本中，实际有风险的比例',
      'risk_recall': '实际有风险的样本中，被正确识别的比例',
      'accuracy': '所有预测正确的样本占总样本的比例'
    };
    return descriptions[key] || '';
  };

  // 通知父组件数据变化
  const notifyChange = (newPresetDimensions, newCustomDimensions) => {
    if (onChange) {
      onChange({
        presetDimensions: newPresetDimensions,
        customDimensions: newCustomDimensions
      });
    }
  };

  // 获取维度图标
  const getDimensionIcon = (key) => {
    const iconMap = {
      'document': <FileTextOutlined />,
      'rule_avg': <BarChartOutlined />,
      'rule_detail': <DatabaseOutlined />
    };
    return iconMap[key] || <DatabaseOutlined />;
  };

  // 切换预设维度启用状态
  const handleTogglePresetDimension = (dimKey, enabled) => {
    const newPresetDimensions = presetDimensions.map(dim => 
      dim.key === dimKey ? { ...dim, enabled } : dim
    );
    setPresetDimensions(newPresetDimensions);
    notifyChange(newPresetDimensions, customDimensions);
  };

  // 切换预设维度下的预设指标启用状态
  const handleTogglePresetMetric = (dimKey, metricKey, enabled) => {
    const newPresetDimensions = presetDimensions.map(dim => {
      if (dim.key === dimKey) {
        return {
          ...dim,
          presetMetrics: dim.presetMetrics.map(m => 
            m.key === metricKey ? { ...m, enabled } : m
          )
        };
      }
      return dim;
    });
    setPresetDimensions(newPresetDimensions);
    notifyChange(newPresetDimensions, customDimensions);
  };

  // 切换自定义维度启用状态
  const handleToggleCustomDimension = (dimKey, enabled) => {
    const newCustomDimensions = customDimensions.map(dim => 
      dim.key === dimKey ? { ...dim, enabled } : dim
    );
    setCustomDimensions(newCustomDimensions);
    notifyChange(presetDimensions, newCustomDimensions);
  };

  // 切换自定义维度下的预设指标启用状态
  const handleToggleCustomPresetMetric = (dimKey, metricKey, enabled) => {
    const newCustomDimensions = customDimensions.map(dim => {
      if (dim.key === dimKey) {
        return {
          ...dim,
          presetMetrics: dim.presetMetrics.map(m => 
            m.key === metricKey ? { ...m, enabled } : m
          )
        };
      }
      return dim;
    });
    setCustomDimensions(newCustomDimensions);
    notifyChange(presetDimensions, newCustomDimensions);
  };

  // 切换自定义指标启用状态
  const handleToggleCustomMetric = (dimKey, metricKey, enabled, isPresetDimension) => {
    if (isPresetDimension) {
      const newPresetDimensions = presetDimensions.map(dim => {
        if (dim.key === dimKey) {
          return {
            ...dim,
            customMetrics: dim.customMetrics.map(m => 
              m.key === metricKey ? { ...m, enabled } : m
            )
          };
        }
        return dim;
      });
      setPresetDimensions(newPresetDimensions);
      notifyChange(newPresetDimensions, customDimensions);
    } else {
      const newCustomDimensions = customDimensions.map(dim => {
        if (dim.key === dimKey) {
          return {
            ...dim,
            customMetrics: dim.customMetrics.map(m => 
              m.key === metricKey ? { ...m, enabled } : m
            )
          };
        }
        return dim;
      });
      setCustomDimensions(newCustomDimensions);
      notifyChange(presetDimensions, newCustomDimensions);
    }
  };

  // 打开添加自定义维度弹窗
  const handleAddCustomDimension = () => {
    setEditingDimension(null);
    setDimensionModalVisible(true);
  };

  // 打开编辑自定义维度弹窗
  const handleEditCustomDimension = (dimension) => {
    setEditingDimension(dimension);
    setDimensionModalVisible(true);
  };

  // 删除自定义维度
  const handleDeleteCustomDimension = (dimKey) => {
    const newCustomDimensions = customDimensions.filter(dim => dim.key !== dimKey);
    setCustomDimensions(newCustomDimensions);
    notifyChange(presetDimensions, newCustomDimensions);
    message.success('删除成功');
  };

  // 保存自定义维度
  const handleSaveCustomDimension = (values) => {
    const dimData = createNewCustomDimension(values);
    
    if (editingDimension) {
      // 编辑模式
      const newCustomDimensions = customDimensions.map(dim => 
        dim.key === editingDimension.key ? { ...dim, ...dimData, key: editingDimension.key } : dim
      );
      setCustomDimensions(newCustomDimensions);
      notifyChange(presetDimensions, newCustomDimensions);
      message.success('修改成功');
    } else {
      // 新增模式
      const newCustomDimensions = [...customDimensions, dimData];
      setCustomDimensions(newCustomDimensions);
      notifyChange(presetDimensions, newCustomDimensions);
      message.success('添加成功');
    }
    
    setDimensionModalVisible(false);
    setEditingDimension(null);
  };

  // 打开添加自定义指标弹窗
  const handleAddCustomMetric = (dimKey, isPresetDimension) => {
    setCurrentDimensionKey(dimKey);
    setCurrentDimensionType(isPresetDimension ? 'preset' : 'custom');
    setEditingMetric(null);
    setMetricModalVisible(true);
  };

  // 打开编辑自定义指标弹窗
  const handleEditCustomMetric = (dimKey, metric, isPresetDimension) => {
    setCurrentDimensionKey(dimKey);
    setCurrentDimensionType(isPresetDimension ? 'preset' : 'custom');
    setEditingMetric(metric);
    setMetricModalVisible(true);
  };

  // 删除自定义指标
  const handleDeleteCustomMetric = (dimKey, metricKey, isPresetDimension) => {
    if (isPresetDimension) {
      const newPresetDimensions = presetDimensions.map(dim => {
        if (dim.key === dimKey) {
          return {
            ...dim,
            customMetrics: dim.customMetrics.filter(m => m.key !== metricKey)
          };
        }
        return dim;
      });
      setPresetDimensions(newPresetDimensions);
      notifyChange(newPresetDimensions, customDimensions);
    } else {
      const newCustomDimensions = customDimensions.map(dim => {
        if (dim.key === dimKey) {
          return {
            ...dim,
            customMetrics: dim.customMetrics.filter(m => m.key !== metricKey)
          };
        }
        return dim;
      });
      setCustomDimensions(newCustomDimensions);
      notifyChange(presetDimensions, newCustomDimensions);
    }
    message.success('删除成功');
  };

  // 保存自定义指标
  const handleSaveCustomMetric = (values) => {
    const metricData = createNewCustomMetric(values);
    
    if (currentDimensionType === 'preset') {
      if (editingMetric) {
        const newPresetDimensions = presetDimensions.map(dim => {
          if (dim.key === currentDimensionKey) {
            return {
              ...dim,
              customMetrics: dim.customMetrics.map(m => 
                m.key === editingMetric.key ? { ...m, ...metricData, key: editingMetric.key } : m
              )
            };
          }
          return dim;
        });
        setPresetDimensions(newPresetDimensions);
        notifyChange(newPresetDimensions, customDimensions);
      } else {
        const newPresetDimensions = presetDimensions.map(dim => {
          if (dim.key === currentDimensionKey) {
            return {
              ...dim,
              customMetrics: [...dim.customMetrics, metricData]
            };
          }
          return dim;
        });
        setPresetDimensions(newPresetDimensions);
        notifyChange(newPresetDimensions, customDimensions);
      }
    } else {
      if (editingMetric) {
        const newCustomDimensions = customDimensions.map(dim => {
          if (dim.key === currentDimensionKey) {
            return {
              ...dim,
              customMetrics: dim.customMetrics.map(m => 
                m.key === editingMetric.key ? { ...m, ...metricData, key: editingMetric.key } : m
              )
            };
          }
          return dim;
        });
        setCustomDimensions(newCustomDimensions);
        notifyChange(presetDimensions, newCustomDimensions);
      } else {
        const newCustomDimensions = customDimensions.map(dim => {
          if (dim.key === currentDimensionKey) {
            return {
              ...dim,
              customMetrics: [...dim.customMetrics, metricData]
            };
          }
          return dim;
        });
        setCustomDimensions(newCustomDimensions);
        notifyChange(presetDimensions, newCustomDimensions);
      }
    }
    
    setMetricModalVisible(false);
    setEditingMetric(null);
    message.success(editingMetric ? '修改成功' : '添加成功');
  };

  // 重置为默认配置
  const handleResetToDefault = () => {
    const defaultConfig = getDefaultConfig();
    setPresetDimensions(defaultConfig.presetDimensions);
    setCustomDimensions(defaultConfig.customDimensions);
    notifyChange(defaultConfig.presetDimensions, defaultConfig.customDimensions);
    message.success('已重置为默认配置');
  };

  // 渲染指标列表
  const renderMetricList = (metrics, dimKey, isPreset, isPresetDimension) => {
    return metrics.map(metric => (
      <div 
        key={metric.key} 
        className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded"
      >
        <div className="flex items-center space-x-3">
          <FunctionOutlined className="text-gray-400" />
          <span className="text-gray-700">{metric.name}</span>
          {metric.key && !metric.key.includes('precision') && !metric.key.includes('recall') && !metric.key.includes('accuracy') && (
            <Tag color="purple" className="text-xs">自定义</Tag>
          )}
          {metric.description && (
            <Tooltip title={metric.description}>
              <InfoCircleOutlined className="text-gray-400 text-sm cursor-help" />
            </Tooltip>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {!isPreset && (
            <Space>
              <Button 
                type="link" 
                size="small" 
                icon={<EditOutlined />}
                onClick={() => handleEditCustomMetric(dimKey, metric, isPresetDimension)}
              >
                编辑
              </Button>
              <Popconfirm
                title="确定要删除这个指标吗？"
                onConfirm={() => handleDeleteCustomMetric(dimKey, metric.key, isPresetDimension)}
                okText="确定"
                cancelText="取消"
              >
                <Button 
                  type="link" 
                  size="small" 
                  danger 
                  icon={<DeleteOutlined />}
                >
                  删除
                </Button>
              </Popconfirm>
            </Space>
          )}
          <Switch
            checked={metric.enabled}
            onChange={(checked) => {
              if (isPreset) {
                if (isPresetDimension) {
                  handleTogglePresetMetric(dimKey, metric.key, checked);
                } else {
                  handleToggleCustomPresetMetric(dimKey, metric.key, checked);
                }
              } else {
                handleToggleCustomMetric(dimKey, metric.key, checked, isPresetDimension);
              }
            }}
            size="small"
            disabled={readOnly}
          />
        </div>
      </div>
    ));
  };

  // 渲染维度卡片
  const renderDimensionCard = (dimension, isPresetDimension) => {
    const dimKey = dimension.key;
    const isActive = dimension.enabled;
    const hasCustomMetrics = dimension.customMetrics && dimension.customMetrics.length > 0;
    const enabledPresetMetrics = dimension.presetMetrics?.filter(m => m.enabled).length || 0;
    const enabledCustomMetrics = dimension.customMetrics?.filter(m => m.enabled).length || 0;

    const header = (
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-lg">{getDimensionIcon(dimKey)}</span>
          <div>
            <span className="font-medium text-gray-800">{dimension.name}</span>
            {dimension.type === 'custom' && (
              <Tag color="blue" className="ml-2">自定义</Tag>
            )}
            {dimension.description && (
              <div className="text-xs text-gray-500 mt-1">{dimension.description}</div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {!isPresetDimension && !readOnly && (
            <Space>
              <Button 
                type="link" 
                size="small" 
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditCustomDimension(dimension);
                }}
              >
                编辑
              </Button>
              <Popconfirm
                title="确定要删除这个维度吗？"
                onConfirm={(e) => {
                  e?.stopPropagation();
                  handleDeleteCustomDimension(dimKey);
                }}
                okText="确定"
                cancelText="取消"
              >
                <Button 
                  type="link" 
                  size="small" 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()}
                >
                  删除
                </Button>
              </Popconfirm>
            </Space>
          )}
          <div className="text-sm text-gray-500">
            <span>预设: {enabledPresetMetrics}/5</span>
            {hasCustomMetrics && (
              <span className="ml-2">自定义: {enabledCustomMetrics}</span>
            )}
          </div>
          <Switch
            checked={isActive}
            onChange={(checked) => {
              if (isPresetDimension) {
                handleTogglePresetDimension(dimKey, checked);
              } else {
                handleToggleCustomDimension(dimKey, checked);
              }
            }}
            size="small"
            disabled={readOnly}
          />
        </div>
      </div>
    );

    return (
      <Card 
        key={dimKey}
        className={`mb-4 ${!isActive ? 'opacity-60' : ''}`}
        title={header}
        size="small"
      >
        {isActive && (
          <div>
            {/* 预设指标 */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <FolderOpenOutlined className="text-yellow-500" />
                  <span className="font-medium text-gray-700">预设指标</span>
                  <span className="text-xs text-gray-400">（{dimension.presetMetrics?.length || 0}个）</span>
                </div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                {renderMetricList(dimension.presetMetrics || [], dimKey, true, isPresetDimension)}
              </div>
            </div>

            {/* 自定义指标 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <FunctionOutlined className="text-purple-500" />
                  <span className="font-medium text-gray-700">自定义指标</span>
                  <span className="text-xs text-gray-400">（{dimension.customMetrics?.length || 0}个）</span>
                </div>
                {!readOnly && (
                  <Button 
                    type="link" 
                    size="small" 
                    icon={<PlusOutlined />}
                    onClick={() => handleAddCustomMetric(dimKey, isPresetDimension)}
                  >
                    添加指标
                  </Button>
                )}
              </div>
              {dimension.customMetrics && dimension.customMetrics.length > 0 ? (
                <div className="bg-gray-50 rounded p-2">
                  {renderMetricList(dimension.customMetrics, dimKey, false, isPresetDimension)}
                </div>
              ) : (
                <div className="text-gray-400 text-sm py-4 text-center bg-gray-50 rounded">
                  暂无自定义指标，点击上方按钮添加
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="dimension-metric-config">
      {/* 配置说明 */}
      <Alert
        message="维度指标配置说明"
        description={
          <div>
            <p>• <strong>预设维度</strong>：系统内置的三个统计维度（单据维度、规则平均维度、规则明细维度）</p>
            <p>• <strong>自定义维度</strong>：用户可基于预设维度创建新的统计维度，支持二次分组</p>
            <p>• <strong>预设指标</strong>：每个维度都包含5个标准指标（无风险精确率、无风险召回率、有风险精确率、有风险召回率、准确率）</p>
            <p>• <strong>自定义指标</strong>：可为每个维度添加特定的评测指标</p>
          </div>
        }
        type="info"
        showIcon
        className="mb-4"
      />

      {/* 预设维度 */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <DatabaseOutlined className="text-blue-500 text-lg" />
            <span className="font-semibold text-gray-800 text-lg">预设维度</span>
            <Tag color="blue">{presetDimensions.length}个</Tag>
          </div>
          {!readOnly && (
            <Button 
              type="link" 
              icon={<ReloadOutlined />}
              onClick={handleResetToDefault}
            >
              重置为默认
            </Button>
          )}
        </div>
        <div>
          {presetDimensions.map(dim => renderDimensionCard(dim, true))}
        </div>
      </div>

      <Divider />

      {/* 自定义维度 */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <SettingOutlined className="text-purple-500 text-lg" />
            <span className="font-semibold text-gray-800 text-lg">自定义维度</span>
            <Tag color="purple">{customDimensions.length}个</Tag>
          </div>
          {!readOnly && (
            <Button 
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCustomDimension}
            >
              添加自定义维度
            </Button>
          )}
        </div>
        
        {customDimensions.length > 0 ? (
          <div>
            {customDimensions.map(dim => renderDimensionCard(dim, false))}
          </div>
        ) : (
          <Card className="text-center py-8 bg-gray-50">
            <DatabaseOutlined className="text-4xl text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">暂无自定义维度</p>
            <p className="text-gray-400 text-sm mb-4">
              自定义维度可基于预设维度进行二次分组统计
            </p>
            {!readOnly && (
              <Button 
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddCustomDimension}
              >
                添加自定义维度
              </Button>
            )}
          </Card>
        )}
      </div>

      {/* 自定义维度弹窗 */}
      <CustomDimensionModal
        visible={dimensionModalVisible}
        onCancel={() => {
          setDimensionModalVisible(false);
          setEditingDimension(null);
        }}
        onSave={handleSaveCustomDimension}
        editingDimension={editingDimension}
        presetDimensions={presetDimensions}
      />

      {/* 自定义指标弹窗 */}
      <MetricConfigModal
        visible={metricModalVisible}
        onCancel={() => {
          setMetricModalVisible(false);
          setEditingMetric(null);
        }}
        onSave={handleSaveCustomMetric}
        editingMetric={editingMetric}
        presetDimensions={presetDimensions}
        customDimensions={customDimensions}
      />
    </div>
  );
};

export default DimensionMetricConfig;
